import { HttpClient } from '@angular/common/http';
import { AssetGroup, AssetGroupAssociationData } from '@app/shared/models/asset-group.model';
import { Observable, of } from 'rxjs';
import { isEqual, uniqBy } from 'lodash';
import { ENDPOINTS } from '@core/constants/endpoints';

export abstract class AssetGroupAssociation {
    protected httpClient: HttpClient;
    protected abstract getAssociationPayload(): AssetGroupAssociationData;

    protected constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;
    }

    public update(companyId: number): Observable<unknown> {
        const payload = this.getAssociationPayload();

        if (isEqual(payload.lineageFrom, payload.lineageTo)) {
            return of(undefined);
        }

        return this.httpClient.post(
            `${ENDPOINTS.apis.companies}/${companyId}/assetstoassetgroups`,
            payload
        );
    }

    protected getAncestorsIds(
        group: AssetGroup,
        list: AssetGroup[],
        ancestors: number[]
    ): number[] {
        const flatList = uniqBy(this.getFlatList(list), 'id');
        if (group.parentId) {
            ancestors.push(group.parentId);
            return this.getAncestorsIds(
                flatList.find((item) => item.id === group.parentId),
                flatList,
                ancestors
            );
        } else {
            return ancestors.reverse();
        }
    }

    protected getChildrenIds(group: AssetGroup, list: AssetGroup[], children: number[]): number[] {
        const flatList = uniqBy(this.getFlatList(list), 'id');
        const groupChildren = flatList.filter((item) => item.parentId === group.id);

        if (groupChildren.length) {
            groupChildren.forEach((child) => {
                children.push(child.id);
                this.getChildrenIds(child, flatList, children);
            });
            return children;
        } else {
            return children;
        }
    }

    protected getData(
        assetGroup: AssetGroup,
        originalList: AssetGroup[],
        updatedList: AssetGroup[]
    ) {
        return {
            assetGroup: {
                original:
                    this.getFlatList(originalList).find((item) => item.id === assetGroup.id) ||
                    assetGroup,
                updated: this.getFlatList(updatedList).find((item) => item.id === assetGroup.id),
            },
            hierarchy: {
                original: this.getHierarchy(originalList),
                updated: this.getHierarchy(updatedList),
            },
        };
    }

    private getFlatList(assetGroups: AssetGroup[]): AssetGroup[] {
        return assetGroups.flatMap((current: AssetGroup) => [
            current,
            ...this.getFlatList(current.children),
        ]);
    }

    private getHierarchy(assetGroups: AssetGroup[]): AssetGroup[] {
        const groups = assetGroups.filter((item) => !item.parentId);
        const flatList = this.getFlatList(assetGroups);

        groups.forEach((group) => {
            group.children = this.getChildren(flatList, group.id);
        });

        return groups;
    }

    private getChildren(groups: AssetGroup[], id: number): AssetGroup[] {
        return uniqBy(
            groups
                .filter((group) => group.parentId === id)
                .map((group) => {
                    return {
                        ...group,
                        children: this.getChildren(groups, group.id),
                    };
                }),
            'id'
        );
    }
}
